import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import Fade from '@mui/material/Fade';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { Eye, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpClient } from '../../common/httpclient';
import BooleanPill from '../../component/common/BooleanPill';
import usePermissions from '../../hooks/usePermissions';
import { ProfilePill } from './List';

// Detalhe do usuário — drawer lateral somente-leitura, mesmo padrão de
// `pages/services/View.js`/`pages/suppliers/View.js` (DESIGN-SYSTEM.md §5.6
// "Side drawer"), preenchido com os campos do dialog de visualização real
// (Nome/Sobrenome/Nome de usuário/Perfil/Senha mascarada/Chave mestra).
//
// `GET /users/{id}` (fv-api `user.Getter.GetById`) não seleciona `p.name`
// (perfil) nem `master_key` — só `FindAll` (`GET /users`, usado pela lista)
// devolve `profile_name`. Por isso o nome do perfil é resolvido aqui no
// cliente a partir do `profile_id` retornado + da lista de perfis já
// carregada por `users/List.js` (prop `profiles`), sem precisar de nenhuma
// mudança de contrato de API. "Chave mestra" já nasce sempre "Não" quando
// aberta a partir daqui — mesma limitação pré-existente do dialog de
// visualização anterior (`user.master_key` nunca vem preenchido nessa
// consulta), preservada 1:1 e não corrigida nesta tarefa (mudança de SELECT
// no backend, fora do escopo de um redesign puramente visual).
function DetailItem({ label, value, muted = false, full = false }) {
    return (
        <Box sx={{ gridColumn: full ? '1 / -1' : 'auto' }}>
            <Typography sx={{ fontSize: '10.5px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'text.disabled', mb: '4px' }}>
                {label}
            </Typography>
            {typeof value === 'string' ? (
                <Typography sx={{ fontSize: '13.5px', fontWeight: muted ? 500 : 600, color: muted ? 'text.disabled' : 'text.primary', lineHeight: 1.4 }}>
                    {value}
                </Typography>
            ) : (
                <Box sx={{ mt: '2px' }}>{value}</Box>
            )}
        </Box>
    );
}

export default function UserViewDialog({ open, onClose, userId, profiles = [] }) {
    const theme = useTheme();
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState({});

    const fetchUser = async (id) => {
        try {
            setLoading(true);
            const response = await httpClient.get(`/users/${id}`);
            setUser(response.data || {});
        } catch (error) {
            console.error('Erro ao carregar dados do usuário:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (userId && open) {
            fetchUser(userId);
        }
    }, [userId, open]);

    const handleClose = () => {
        setUser({});
        setLoading(true);
        onClose();
    };

    const profileName = user.profile_name || profiles.find((p) => p.id === user.profile_id)?.name || '—';
    const fullName = user.name ? `${user.name} ${user.last_name || ''}`.trim() : 'Detalhes do usuário';

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={handleClose}
            slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' } } }}
            PaperProps={{
                sx: {
                    width: { xs: '100%', sm: 560, md: 640 }, maxWidth: '100vw',
                    borderRadius: 0,
                    boxShadow: theme.palette.mode === 'dark' ? '-8px 0 32px rgba(0,0,0,0.4)' : '-8px 0 32px rgba(0,0,0,0.12)',
                    borderLeft: '1px solid', borderColor: 'divider',
                    display: 'flex', flexDirection: 'column', overflow: 'hidden',
                },
            }}
        >
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Box sx={{ p: '22px 22px 16px', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.25, flexShrink: 0 }}>
                    <Box sx={{ minWidth: 0 }}>
                        <Box sx={{
                            width: 38,
                            height: 38,
                            borderRadius: '11px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'info.bg',
                            color: 'primary.main',
                            mb: '10px',
                        }}>
                            <Eye size={18} strokeWidth={2} />
                        </Box>
                        <Typography sx={{ fontSize: '17px', fontWeight: 800, mb: '4px' }}>
                            {loading ? 'Carregando…' : fullName}
                        </Typography>
                        <Typography sx={{ fontSize: '12.5px', color: 'text.secondary' }}>
                            Detalhes do usuário cadastrado.
                        </Typography>
                    </Box>
                    <IconButton onClick={handleClose} size="small" aria-label="Fechar">
                        <X size={15} strokeWidth={2} />
                    </IconButton>
                </Box>

                <Box sx={{ p: '20px 22px', flex: 1, overflowY: 'auto' }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : (
                        <Fade in={!loading} timeout={400}>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '14px 18px' }}>
                                <DetailItem label="Nome" value={user.name || '—'} />
                                <DetailItem label="Sobrenome" value={user.last_name || '—'} muted={!user.last_name} />
                                <DetailItem label="Nome de usuário" value={user.username || '—'} />
                                <DetailItem label="Perfil" value={<ProfilePill label={profileName} />} />
                                <DetailItem label="Senha" value="••••••••" muted />
                                <DetailItem label="Chave mestra" value={<BooleanPill value={!!user.master_key} />} />
                            </Box>
                        </Fade>
                    )}
                </Box>

                <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: 1.25, justifyContent: 'flex-end', flexShrink: 0 }}>
                    <Button variant="outlined" onClick={handleClose}>Fechar</Button>
                    {hasPermission('user.update') && (
                        <Button
                            variant="contained"
                            onClick={() => {
                                const targetId = userId;
                                handleClose();
                                navigate(`/users/edit/${targetId}`);
                            }}
                        >
                            Editar
                        </Button>
                    )}
                </Box>
            </Box>
        </Drawer>
    );
}
